import type { IrregularDeltaItem, DeltaMeterValue, HandleType } from '../types/data-error-types';

export interface SmartSuggestion {
  action: HandleType;
  confidence: 'high' | 'medium' | 'low';
  title: string;
  reason: string;
  evidence: string[];
}

/**
 * Analyzes an irregular delta record and its surrounding meter values to suggest
 * the most likely correct action. Uses sequence-based analysis — not just the
 * delta value in isolation, but whether readings after the trigger continue from
 * the trigger value (replacement) or revert to the pre-trigger level (corrupt spike).
 *
 * Returns null when no pattern matches confidently.
 */
export function analyzeDelta(
  item: IrregularDeltaItem,
  meterValues: DeltaMeterValue[]
): SmartSuggestion | null {
  if (meterValues.length === 0) return null;

  const sorted = [...meterValues].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
  );

  const triggerIdx = sorted.findIndex(m => m.meterValueId === item.toMeterValueId);
  if (triggerIdx === -1) return null;

  const trigger = sorted[triggerIdx];
  const before = sorted.slice(0, triggerIdx);
  const after = sorted.slice(triggerIdx + 1);
  const prev = before[before.length - 1] ?? null;
  const next = after[0] ?? null;

  const triggerEnergy = trigger.energy ?? null;
  const prevEnergy = prev?.energy ?? null;
  const nextEnergy = next?.energy ?? null;

  // Virtual meter → simulator/test data
  if (trigger.isVirtualMeter) {
    return {
      action: 'Replacement',
      confidence: 'medium',
      title: 'Virtual meter reading',
      reason: 'This reading comes from a simulated or virtual meter.',
      evidence: [
        'isVirtualMeter = true',
        'Virtual meter readings should be marked as replacement',
      ],
    };
  }

  // Determine whether readings after the trigger continue from the trigger value
  // or revert back toward the pre-trigger level.
  // "closer to trigger" means the meter continued from the new value → replacement/reset
  // "closer to prev" means it was a transient spike → corrupt reading
  let continuesFromTrigger: boolean | null = null;
  if (nextEnergy !== null && triggerEnergy !== null && prevEnergy !== null) {
    const distToTrigger = Math.abs(nextEnergy - triggerEnergy);
    const distToPrev = Math.abs(nextEnergy - prevEnergy);
    continuesFromTrigger = distToTrigger < distToPrev;
  }

  // ── Pattern 0: Per-session energy metering (TransactionBegin from zero) ──
  // OCPP chargers that meter energy per-session reset their counter to 0 at the
  // start of every transaction. A TransactionBegin reading near zero is always
  // expected charger behaviour — never a hardware fault or meter replacement.
  // Must be checked before Pattern 1 which would otherwise flag the negative
  // delta as a replacement.
  if (
    trigger.readingContext === 'TransactionBegin' &&
    triggerEnergy !== null &&
    triggerEnergy < 1.0
  ) {
    return {
      action: 'Ignore',
      confidence: 'high',
      title: 'Per-session metering — counter resets to zero each session',
      reason: `This charger measures energy per-session and always resets its counter to near zero at TransactionBegin. The negative delta is a known false alarm, not a real data error.`,
      evidence: [
        `Reading context: TransactionBegin`,
        `Energy at session start: ${triggerEnergy.toFixed(3)} kWh (expected counter reset)`,
        `Delta: ${item.delta.toFixed(3)} kWh (reflects reset, not physical energy loss)`,
        ...(prevEnergy !== null ? [`Previous reading: ${prevEnergy.toFixed(3)} kWh`] : []),
      ],
    };
  }

  // ── Pattern 1: Meter reset / replacement ─────────────────────────────────
  // Energy dropped AND subsequent readings continue incrementing from the new low.
  // Example: 100 → 1 → 2 → 3 → ...  (charger was physically replaced)
  if (
    item.delta < 0 &&
    triggerEnergy !== null &&
    prevEnergy !== null &&
    triggerEnergy < prevEnergy &&
    (continuesFromTrigger === true || next === null)
  ) {
    return {
      action: 'Replacement',
      // High confidence only when post-trigger sequence confirms continuity from new value.
      // Medium when trigger is the last reading — only the negative delta as evidence.
      confidence: continuesFromTrigger === true ? 'high' : 'medium',
      title: 'Meter reset or replacement detected',
      reason: `Energy dropped from ${prevEnergy.toFixed(3)} kWh to ${triggerEnergy.toFixed(3)} kWh and subsequent readings continue from the lower value — consistent with a meter being reset or physically replaced.`,
      evidence: [
        `Energy before: ${prevEnergy.toFixed(3)} kWh`,
        `Trigger reading: ${triggerEnergy.toFixed(3)} kWh (Δ ${item.delta.toFixed(3)} kWh)`,
        ...(nextEnergy !== null
          ? [`Next reading: ${nextEnergy.toFixed(3)} kWh (increments from new value)`]
          : []),
        `Duration between readings: ${fmtSec(item.durationSeconds)}`,
      ],
    };
  }

  // ── Pattern 2: Single corrupt spike then revert ───────────────────────────
  // Energy jumped far but AFTER readings returned to the expected sequence.
  // Example: 100 → 10000 → 101 → 102 → ...  (bad reading, meter was fine)
  // Minimum delta of 50 kWh to avoid false positives on small legitimate dips.
  if (
    Math.abs(item.delta) > 50 &&
    continuesFromTrigger === false &&
    prevEnergy !== null &&
    triggerEnergy !== null
  ) {
    return {
      action: 'Delete',
      confidence: 'high',
      title: 'Single corrupt reading — spike then revert',
      reason: `Energy spiked to ${triggerEnergy.toFixed(3)} kWh but subsequent readings returned to the expected sequence near ${prevEnergy.toFixed(3)} kWh, suggesting this is an isolated bad reading.`,
      evidence: [
        `Energy before: ${prevEnergy.toFixed(3)} kWh`,
        `Trigger (spike): ${triggerEnergy.toFixed(3)} kWh (Δ ${item.delta.toFixed(3)} kWh)`,
        ...(nextEnergy !== null
          ? [`Next reading: ${nextEnergy.toFixed(3)} kWh (reverted toward pre-spike level)`]
          : []),
      ],
    };
  }

  // ── Pattern 3: Large sustained jump ──────────────────────────────────────
  // Huge delta AND after-readings continue from the new level — may be real or corrupt.
  if (Math.abs(item.delta) > 100 && continuesFromTrigger === true) {
    return {
      action: 'ManualCorrection',
      confidence: 'medium',
      title: 'Large energy jump — manual review needed',
      reason: `A delta of ${item.delta.toFixed(3)} kWh was recorded and subsequent readings continue from the new level. This may be a legitimate large session or a data integrity issue.`,
      evidence: [
        `Delta: ${item.delta.toFixed(3)} kWh`,
        `Duration: ${fmtSec(item.durationSeconds)}`,
        item.sessionId !== null
          ? `Session ID: ${item.sessionId}`
          : 'No associated session',
      ],
    };
  }

  // ── Pattern 4: Session boundary reading ──────────────────────────────────
  // Reading context is a transaction boundary — delta likely represents session energy.
  if (
    (trigger.readingContext === 'TransactionEnd' ||
      trigger.readingContext === 'TransactionBegin') &&
    item.delta >= 0 &&
    item.delta <= 100  // > 100 kWh at a boundary falls through to Pattern 3 (ManualCorrection)
  ) {
    return {
      action: 'Approve',
      confidence: 'medium',
      title: 'Session boundary reading',
      reason: `This delta was recorded at a transaction ${trigger.readingContext === 'TransactionEnd' ? 'end' : 'start'} boundary. A delta of ${item.delta.toFixed(3)} kWh is within a plausible session energy range.`,
      evidence: [
        `Reading context: ${trigger.readingContext}`,
        `Delta: ${item.delta.toFixed(3)} kWh`,
        ...(item.sessionId !== null ? [`Session ID: ${item.sessionId}`] : []),
      ],
    };
  }

  // ── Pattern 5: Negative delta (no continuity data available) ─────────────
  if (item.delta < 0) {
    return {
      action: 'Replacement',
      confidence: 'medium',
      title: 'Negative energy delta',
      reason: `Energy cannot physically decrease between readings. A delta of ${item.delta.toFixed(3)} kWh suggests a meter replacement, meter reset, or data corruption.`,
      evidence: [
        `Delta: ${item.delta.toFixed(3)} kWh (physically impossible)`,
        `Duration: ${fmtSec(item.durationSeconds)}`,
      ],
    };
  }

  return null;
}

function fmtSec(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}
